import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ModeToggle } from '@/components/theme-toggle';
import { NotificationCenter } from '@/components/NotificationCenter';
import { 
  LayoutDashboard, 
  FolderOpen, 
  Kanban, 
  BarChart3, 
  Users,
  LogOut,
  Settings,
  FileText,
  Activity,
  Calendar,
  Search,
  Files,
  Zap,
  Link2
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  currentView: string;
  onViewChange: (view: 'dashboard' | 'projects' | 'kanban' | 'gantt' | 'users' | 'reports' | 'activities' | 'calendar' | 'search' | 'templates' | 'automation' | 'integrations') => void;
}

export const Layout: React.FC<LayoutProps> = ({ children, currentView, onViewChange }) => {
  const { user, logout } = useAuth();

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'projects', label: 'Projetos', icon: FolderOpen },
    { id: 'kanban', label: 'Kanban', icon: Kanban },
    { id: 'gantt', label: 'Gantt', icon: BarChart3 },
    { id: 'calendar', label: 'Calendário', icon: Calendar },
    { id: 'reports', label: 'Relatórios', icon: FileText },
    { id: 'activities', label: 'Atividades', icon: Activity },
    { id: 'search', label: 'Busca', icon: Search },
    { id: 'templates', label: 'Templates', icon: Files },
    { id: 'automation', label: 'Automação', icon: Zap },
    { id: 'integrations', label: 'Integrações', icon: Link2 },
    { id: 'users', label: 'Usuários', icon: Users }
  ];

  const mockUserAvatars = {
    '1': 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face',
    '2': 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face',
    '3': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">PM</span>
                </div>
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">Project Manager</h1>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden lg:flex space-x-1">
              {navigationItems.slice(0, 4).map(item => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant={currentView === item.id ? 'default' : 'ghost'}
                    onClick={() => onViewChange(item.id as any)}
                    className={`${
                      currentView === item.id 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    size="sm"
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Button>
                );
              })}
            </nav>

            {/* Secondary Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navigationItems.slice(4, 8).map(item => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant={currentView === item.id ? 'default' : 'ghost'}
                    onClick={() => onViewChange(item.id as any)}
                    className={`${
                      currentView === item.id 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    size="sm"
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {item.label}
                  </Button>
                );
              })}
            </div>

            {/* Advanced Features */}
            <div className="hidden xl:flex items-center space-x-1">
              {navigationItems.slice(8, 11).map(item => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.id}
                    variant={currentView === item.id ? 'default' : 'ghost'}
                    onClick={() => onViewChange(item.id as any)}
                    className={`${
                      currentView === item.id 
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    size="sm"
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {item.label}
                  </Button>
                );
              })}
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewChange('search')}
                className={currentView === 'search' ? 'bg-blue-100 text-blue-600' : ''}
              >
                <Search className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewChange('users')}
                className={currentView === 'users' ? 'bg-blue-100 text-blue-600' : ''}
              >
                <Users className="w-4 h-4" />
              </Button>
              <NotificationCenter />
              <ModeToggle />

              {/* User Menu */}
              <div className="flex items-center space-x-3">
                <Avatar className="w-8 h-8">
                  <AvatarImage 
                    src={mockUserAvatars[user?.id as keyof typeof mockUserAvatars]} 
                    alt={user?.name} 
                  />
                  <AvatarFallback>
                    {user?.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block">
                  <div className="text-sm font-medium text-gray-700">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={logout}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="lg:hidden bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1 py-2 overflow-x-auto">
            {navigationItems.map(item => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.id}
                  variant={currentView === item.id ? 'default' : 'ghost'}
                  onClick={() => onViewChange(item.id as any)}
                  className={`${
                    currentView === item.id 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  } whitespace-nowrap`}
                  size="sm"
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {item.label}
                </Button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        {children}
      </main>
    </div>
  );
};